import type { DeleteEnterpriseRiskMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

// The tRPC delete returns no content (204); the V2 GraphQL mutation reported a
// single affected row, which the delete page checks to unmount.
const mapTrpcResponseToGraphQL = (): DeleteEnterpriseRiskMutation => ({
  deleteChildEnterpriseRisk: {
    __typename: 'GenericMutationOutput',
    affected_rows: 1,
  },
});

/**
 * tRPC implementation of the delete-enterprise-risk mutation (RSP-6498). NOT
 * imported outside this directory — consumers use the flag-gated
 * `useDeleteEnterpriseRisk` wrapper.
 */
export const useDeleteEnterpriseRiskTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.enterpriseRisk.delete.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.register.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.getById.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.getByTier.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    deleteEnterpriseRisk: async (
      id: string
    ): Promise<DeleteEnterpriseRiskMutation> => {
      await mutation.mutateAsync({ Id: id });

      return mapTrpcResponseToGraphQL();
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
